import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  FormHelperText,
  Checkbox,
  Alert,
  CircularProgress,
} from '@mui/material';
import { PaymentDetails as PaymentDetailsType } from '../../types/order';
import ApiService from '../../services/api';

interface PaymentDetailsProps {
  onDataChange: (data: PaymentDetailsType) => void;
  showErrors?: boolean;
  /** Raw form state kept in the parent (memory only) so Back/Next doesn't lose what was typed. */
  draft?: PaymentDraft | null;
  onDraftChange?: (draft: PaymentDraft) => void;
}

export interface PaymentDraft {
  paymentMethod: 'credit_card' | 'direct_debit';
  creditCard: { card_number: string; expiry_month: string; expiry_year: string; cvv: string; name_on_card: string };
  directDebit: { bsb: string; account_number: string; account_name: string };
  termsAccepted: boolean;
}

function PaymentDetailsComponent({ onDataChange, showErrors = false, draft, onDraftChange }: PaymentDetailsProps) {
  const [paymentMethod, setPaymentMethod] = useState<'credit_card' | 'direct_debit'>(draft?.paymentMethod ?? 'credit_card');
  const [creditCard, setCreditCard] = useState(draft?.creditCard ?? {
    card_number: '',
    expiry_month: '',
    expiry_year: '',
    cvv: '',
    name_on_card: '',
  });
  const [directDebit, setDirectDebit] = useState(draft?.directDebit ?? {
    bsb: '',
    account_number: '',
    account_name: '',
  });
  const [termsAccepted, setTermsAccepted] = useState(draft?.termsAccepted ?? false);
  const [ezidebitTerms, setEzidebitTerms] = useState<{
    credit_card_terms: string;
    direct_debit_terms: string;
  } | null>(null);
  const [termsLoading, setTermsLoading] = useState(true);
  const [termsError, setTermsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setTermsLoading(true);
    setTermsError(null);
    ApiService.getEzidebitPaymentTerms()
      .then((data) => {
        if (!cancelled) {
          setEzidebitTerms({
            credit_card_terms: data.credit_card_terms ?? '',
            direct_debit_terms: data.direct_debit_terms ?? '',
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTermsError('Could not load payment terms. You can still continue; contact us if you need a copy.');
          setEzidebitTerms({ credit_card_terms: '', direct_debit_terms: '' });
        }
      })
      .finally(() => {
        if (!cancelled) setTermsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const prevMethod = React.useRef(paymentMethod);
  useEffect(() => {
    if (prevMethod.current !== paymentMethod) {
      prevMethod.current = paymentMethod;
      setTermsAccepted(false);
    }
  }, [paymentMethod]);

  useEffect(() => {
    onDraftChange?.({ paymentMethod, creditCard, directDebit, termsAccepted });
  }, [paymentMethod, creditCard, directDebit, termsAccepted, onDraftChange]);

  const monthNum = parseInt(creditCard.expiry_month);
  const yearNum = parseInt(creditCard.expiry_year);
  const monthValid = monthNum >= 1 && monthNum <= 12;
  const yearValid = creditCard.expiry_year.length === 4 && yearNum >= new Date().getFullYear();

  useEffect(() => {
    onDataChange({
      payment_method: paymentMethod,
      credit_card: paymentMethod === 'credit_card' ? {
        card_number: creditCard.card_number,
        expiry_month: monthValid ? parseInt(creditCard.expiry_month) : 0,
        expiry_year: yearValid ? parseInt(creditCard.expiry_year) : 0,
        cvv: creditCard.cvv,
        name_on_card: creditCard.name_on_card,
      } : undefined,
      direct_debit: paymentMethod === 'direct_debit' ? {
        bsb: directDebit.bsb,
        account_number: directDebit.account_number,
        account_name: directDebit.account_name,
      } : undefined,
      terms_accepted: termsAccepted,
    });
  }, [paymentMethod, creditCard, monthValid, yearValid, directDebit, termsAccepted, onDataChange]);

  const activeEzidebitTerms =
    paymentMethod === 'credit_card'
      ? ezidebitTerms?.credit_card_terms ?? ''
      : ezidebitTerms?.direct_debit_terms ?? '';

  const formatCardNumber = (value: string) => {
    // Remove all non-digits
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    // Add spaces every 4 digits
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };
  
  const formatBSB = (value: string) => {
    const v = value.replace(/\D/g, '');
    if (v.length <= 6) {
      return v;
    }
    return v.substring(0, 6);
  };
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Payment Information
      </Typography>
      
      <FormControl component="fieldset" sx={{ mb: 3 }}>
        <FormLabel component="legend">Payment Method</FormLabel>
        <RadioGroup
          row
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value as 'credit_card' | 'direct_debit')}
        >
          <FormControlLabel value="credit_card" control={<Radio />} label="Credit Card" />
          <FormControlLabel value="direct_debit" control={<Radio />} label="Direct Debit" />
        </RadioGroup>
      </FormControl>
      
      {paymentMethod === 'credit_card' && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Card Number"
              value={creditCard.card_number}
              onChange={(e) => setCreditCard({ ...creditCard, card_number: formatCardNumber(e.target.value) })}
              placeholder="1234 5678 9012 3456"
              inputProps={{ maxLength: 19 }}
              error={showErrors && !creditCard.card_number.trim()}
              helperText={showErrors && !creditCard.card_number.trim() ? 'Card number is required' : undefined}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              required
              label="Expiry Month"
              placeholder="MM"
              value={creditCard.expiry_month}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 2);
                setCreditCard({ ...creditCard, expiry_month: digits });
              }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 2, autoComplete: 'cc-exp-month' }}
              error={showErrors && !monthValid}
              helperText={showErrors && !monthValid ? 'Enter a month from 1 to 12' : undefined}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              required
              label="Expiry Year"
              placeholder="YYYY"
              value={creditCard.expiry_year}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
                setCreditCard({ ...creditCard, expiry_year: digits });
              }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 4, autoComplete: 'cc-exp-year' }}
              error={showErrors && !yearValid}
              helperText={showErrors && !yearValid ? 'Enter a 4-digit year, e.g. 2028' : undefined}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              required
              label="CVV"
              type="password"
              value={creditCard.cvv}
              onChange={(e) => setCreditCard({ ...creditCard, cvv: e.target.value.replace(/\D/g, '').substring(0, 4) })}
              inputProps={{ maxLength: 4 }}
              error={showErrors && !creditCard.cvv.trim()}
              helperText={showErrors && !creditCard.cvv.trim() ? 'CVV is required' : undefined}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Name on Card"
              value={creditCard.name_on_card}
              onChange={(e) => setCreditCard({ ...creditCard, name_on_card: e.target.value })}
              error={showErrors && !creditCard.name_on_card.trim()}
              helperText={showErrors && !creditCard.name_on_card.trim() ? 'Name on card is required' : undefined}
            />
          </Grid>
        </Grid>
      )}
      
      {paymentMethod === 'direct_debit' && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              required
              label="BSB"
              value={directDebit.bsb}
              onChange={(e) => setDirectDebit({ ...directDebit, bsb: formatBSB(e.target.value) })}
              placeholder="123456"
              inputProps={{ maxLength: 6 }}
              error={showErrors && directDebit.bsb.replace(/\D/g, '').length !== 6}
              helperText={showErrors && directDebit.bsb.replace(/\D/g, '').length !== 6 ? 'BSB must be 6 digits' : undefined}
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              required
              label="Account Number"
              value={directDebit.account_number}
              onChange={(e) => setDirectDebit({ ...directDebit, account_number: e.target.value.replace(/\D/g, '') })}
              inputProps={{ maxLength: 10 }}
              error={showErrors && !directDebit.account_number.trim()}
              helperText={showErrors && !directDebit.account_number.trim() ? 'Account number is required' : undefined}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Account Name"
              value={directDebit.account_name}
              onChange={(e) => setDirectDebit({ ...directDebit, account_name: e.target.value })}
              error={showErrors && !directDebit.account_name.trim()}
              helperText={showErrors && !directDebit.account_name.trim() ? 'Account name is required' : undefined}
            />
          </Grid>
        </Grid>
      )}
      
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          {paymentMethod === 'credit_card' ? 'Credit card' : 'Direct debit'} — Ezidebit terms and conditions
        </Typography>
        {termsLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={22} />
            <Typography variant="body2" color="text.secondary">
              Loading terms…
            </Typography>
          </Box>
        ) : (
          <>
            {termsError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {termsError}
              </Alert>
            )}
            <Box
              sx={{
                maxHeight: 360,
                overflow: 'auto',
                p: 2,
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'action.hover',
              }}
            >
              {activeEzidebitTerms.trim() ? (
                <Typography component="div" variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {activeEzidebitTerms}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Terms text is not configured for this payment method. Administrators can add it under Settings →
                  Ordering.
                </Typography>
              )}
            </Box>
          </>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          Your payment information is encrypted and securely stored. We use industry-standard encryption to protect
          your sensitive data.
        </Alert>

        <FormControlLabel
          control={
            <Checkbox
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              required
            />
          }
          label="I have read and accept the Ezidebit terms and conditions shown above"
        />
        {showErrors && !termsAccepted && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            You must accept the terms and conditions to continue
          </FormHelperText>
        )}
      </Box>
    </Box>
  );
}

export default PaymentDetailsComponent;

